// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Badge, Box, Button, Card, Center, Flex, Group, Modal, Stack, Text, Title } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { getReferenceString } from '@medplum/core';
import type {
  Appointment,
  ChargeItem,
  Claim,
  Condition,
  Encounter,
  MedicationRequest,
  MedicationStatement,
  Observation,
  Patient,
  Practitioner,
  Provenance,
  Reference,
  ServiceRequest,
} from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import * as Sentry from '@sentry/react';
import { IconFilePlus } from '@tabler/icons-react';
import dayjs from 'dayjs';
import type { Dispatch, JSX, MutableRefObject, RefObject, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRole } from '../../hooks/useRole';
import { ChartNoteStatus } from '../../types/encounter';
import { findExistingCondition } from '../../utils/conditions';
import { showErrorNotification } from '../../utils/notifications';
import { hasPermission } from '../../utils/permissions';
import { isReferralMedicationRequest } from '../../utils/referral-pdf';
import { withTenantScope } from '../../utils/tenant';
import { BillingTab } from './BillingTab';
import { DiagnosesSection } from './DiagnosesSection';
import { EncounterNotesSection } from './EncounterNotesSection';
import type {
  ChartFormValues,
  ComorbidityFormRow,
  DiagnosisFormValue,
  FormCodeableConcept,
  LoadedNoteObservations,
  MedicationFormRow,
  NoteCode,
  SymptomFormRow,
} from './encounter-chart-types';
import { MIN_DIAGNOSIS_ENTRIES, NOTE_CODES } from './encounter-chart-types';
import { NewPrescriptionModal } from './NewPrescriptionModal';
import { OrderNewTestModal } from './OrderNewTestModal';
import { PatientConditionSection } from './PatientConditionSection';
import { ReferralModal, type ReferralNoteDiagnosis } from './ReferralModal';
import { SignAddendum } from './SignAddendum';
import { SignLockDialog } from './SignLockDialog';
import { SupplementalInfoSection } from './SupplementalInfoSection';

export type {
  ChartFormValues,
  ComorbidityFormRow,
  DiagnosisFormValue,
  FormCodeableConcept,
  LoadedNoteObservations,
  MedicationFormRow,
  NoteCode,
  SymptomFormRow,
} from './encounter-chart-types';
export { NOTE_CODES } from './encounter-chart-types';

const BRAND = '#640040';
const ORDER_BG = 'var(--mantine-color-gray-0)';
const PRINCIPAL_USE_CODING = [{ system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode', code: 'principal' }];
const SECONDARY_USE_CODING = [{ system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode', code: 'secondary' }];
const POSITIONAL_APNEA_EXTENSION = 'https://polarissleep.com/fhir/StructureDefinition/suspected-positional-apnea';
const CONDITION_CATEGORY_SYSTEM = 'https://polarissleep.com/fhir/condition-category';
const SYMPTOM_CATEGORY_CODE = 'symptom';
const COMORBIDITY_CATEGORY_CODE = 'comorbidity';
const NOTE_OBSERVATION_SYSTEM = 'https://polarissleep.com/fhir/observation-type';

function sortEncounterDiagnoses(diagnoses: Encounter['diagnosis']): NonNullable<Encounter['diagnosis']> {
  return [...(diagnoses ?? [])].sort((left, right) => {
    const leftCode = left.use?.coding?.[0]?.code;
    const rightCode = right.use?.coding?.[0]?.code;
    if (leftCode === 'principal' && rightCode !== 'principal') {
      return -1;
    }
    if (rightCode === 'principal' && leftCode !== 'principal') {
      return 1;
    }
    return (left.rank ?? Number.MAX_SAFE_INTEGER) - (right.rank ?? Number.MAX_SAFE_INTEGER);
  });
}

function ensureMinimumDiagnoses(diagnoses: DiagnosisFormValue[]): DiagnosisFormValue[] {
  const nextDiagnoses = [...diagnoses];
  while (nextDiagnoses.length < MIN_DIAGNOSIS_ENTRIES) {
    nextDiagnoses.push({ concept: undefined });
  }
  return nextDiagnoses;
}

function isoToTimeString(iso: string | undefined): string {
  if (!iso) {
    return '';
  }
  try {
    const date = new Date(iso);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  } catch {
    return '';
  }
}

function mergeTimeToISO(timeStr: string, baseIso: string | undefined): string | undefined {
  if (!timeStr) {
    return baseIso;
  }
  const base = baseIso ? new Date(baseIso) : new Date();
  const [hoursStr, minutesStr] = timeStr.split(':');
  const hours = Number(hoursStr);
  const minutes = Number(minutesStr);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return baseIso;
  }
  base.setHours(hours, minutes, 0, 0);
  return base.toISOString();
}

function buildInitialValues(encounter: Encounter, appointment: Appointment | undefined): ChartFormValues {
  return {
    startTime: isoToTimeString(encounter.period?.start ?? appointment?.start),
    endTime: isoToTimeString(encounter.period?.end ?? appointment?.end),
    treatmentPlan: '',
    hpi: '',
    vitalSigns: '',
    reviewSymptoms: '',
    exam: '',
    noteForPatient: '',
    noteForReferring: '',
    chiefComplaint: encounter.reasonCode?.[0]?.text ?? '',
    assessment: '',
    medications: [],
    symptoms: [],
    comorbidities: [],
    diagnoses: ensureMinimumDiagnoses(
      sortEncounterDiagnoses(encounter.diagnosis).map((diagnosis) => ({
        id: diagnosis.condition?.reference?.split('/')[1],
        concept: undefined,
        recordedDate: undefined,
      }))
    ),
    suspectedPositionalApnea: encounter.extension?.find((e) => e.url === POSITIONAL_APNEA_EXTENSION)?.valueString ?? '',
  };
}

export interface EncounterChartFormProps {
  encounter: Encounter;
  appointment: Appointment | undefined;
  patient: Patient;
  chargeItems: ChargeItem[];
  claim: Claim | undefined;
  practitioner: Practitioner | undefined;
  provenances: Provenance[];
  chartNoteStatus: ChartNoteStatus;
  isLocked: boolean;
  saveChartRef?: RefObject<() => Promise<void>>;
  onSign: (practitioner: Reference<Practitioner>, lock: boolean) => Promise<void>;
  setEncounter: Dispatch<SetStateAction<Encounter | undefined>>;
  setPractitioner: Dispatch<SetStateAction<Practitioner | undefined>>;
  setChargeItems: Dispatch<SetStateAction<ChargeItem[]>>;
  setClaim: Dispatch<SetStateAction<Claim | undefined>>;
}

export const EncounterChartForm = ({
  encounter,
  appointment,
  patient,
  chargeItems,
  claim,
  practitioner,
  provenances,
  chartNoteStatus,
  isLocked,
  saveChartRef,
  onSign,
  setEncounter,
  setPractitioner,
  setChargeItems,
  setClaim,
}: EncounterChartFormProps): JSX.Element => {
  const medplum = useMedplum();
  const [signOpened, { open: openSign, close: closeSign }] = useDisclosure(false);
  const [newTestOpened, { open: openNewTest, close: closeNewTest }] = useDisclosure(false);
  const [newPrescriptionOpened, { open: openNewPrescription, close: closeNewPrescription }] = useDisclosure(false);
  const [referralOpened, { open: openReferral, close: closeReferral }] = useDisclosure(false);
  const { role, isAdmin } = useRole();
  // UX gate only; the MedicationRequest write in the role's AccessPolicy is the security boundary.
  const canCreateReferral = hasPermission({ role, isAdmin, permission: 'create-referral-order' });
  // The referral modal's in-form signature is authoritative, so a locked note does not block referrals.
  const referralDisabled = !canCreateReferral;

  const [serviceRequests, setServiceRequests] = useState<ServiceRequest[]>([]);
  const [medicationRequests, setMedicationRequests] = useState<MedicationRequest[]>([]);
  const referralRequests = medicationRequests.filter(isReferralMedicationRequest);
  const therapyRequests = medicationRequests.filter((request) => !isReferralMedicationRequest(request));

  const refreshOrders = useCallback(async (): Promise<void> => {
    const [srBundle, mrBundle] = await Promise.all([
      medplum.search('ServiceRequest', `encounter=Encounter/${encounter.id}`),
      medplum.search('MedicationRequest', `encounter=Encounter/${encounter.id}`),
    ]);
    setServiceRequests(
      (srBundle.entry ?? []).map((e: import('@medplum/fhirtypes').BundleEntry) => e.resource as ServiceRequest)
    );
    setMedicationRequests(
      (mrBundle.entry ?? []).map((e: import('@medplum/fhirtypes').BundleEntry) => e.resource as MedicationRequest)
    );
  }, [medplum, encounter.id]);

  useEffect(() => {
    refreshOrders().catch(console.error);
  }, [refreshOrders]);

  const handleCloseNewTest = (): void => {
    closeNewTest();
    refreshOrders().catch(console.error);
  };

  const handleCloseNewPrescription = (): void => {
    closeNewPrescription();
    refreshOrders().catch(console.error);
  };

  // Tracks FHIR-persisted resource IDs so we can detect deletions on save
  const medicationIdsRef = useRef<Set<string>>(new Set());
  const symptomIdsRef = useRef<Set<string>>(new Set());
  const comorbidityIdsRef = useRef<Set<string>>(new Set());
  // Maps NoteCode → Observation FHIR ID for upsert on save
  const noteObsIdsRef = useRef<Map<NoteCode, string>>(new Map());

  const form = useForm<ChartFormValues>({
    initialValues: buildInitialValues(encounter, appointment),
  });
  // Live note diagnoses so ones added on this page show under "This encounter" in the referral modal.
  const referralNoteDiagnoses = useMemo<ReferralNoteDiagnosis[]>(
    () =>
      form.values.diagnoses.map((diagnosis) => {
        const coding = diagnosis.concept?.coding?.[0];
        return { code: coding?.code, system: coding?.system, text: coding?.display ?? diagnosis.concept?.text };
      }),
    [form.values.diagnoses]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-hydrate encounter-derived fields when encounter ID changes; async-loaded fields preserved in merged object
  useEffect(() => {
    const values = buildInitialValues(encounter, appointment);
    const merged = {
      ...values,
      assessment: form.values.assessment,
      hpi: form.values.hpi,
      treatmentPlan: form.values.treatmentPlan,
      vitalSigns: form.values.vitalSigns,
      reviewSymptoms: form.values.reviewSymptoms,
      exam: form.values.exam,
      noteForPatient: form.values.noteForPatient,
      noteForReferring: form.values.noteForReferring,
      medications: form.values.medications,
      symptoms: form.values.symptoms,
      comorbidities: form.values.comorbidities,
      diagnoses: form.values.diagnoses,
    };
    form.setValues(merged);
    form.resetDirty(merged);
  }, [encounter.id]);

  const saveToFhir = useCallback(
    async (values: ChartFormValues): Promise<void> => {
      try {
        // ── 1. Encounter ──────────────────────────────────────────────────────────
        const upsertDiagnosisCondition = async (value: DiagnosisFormValue): Promise<string | undefined> => {
          if (!value.concept) {
            return value.id ? `Condition/${value.id}` : undefined;
          }
          const resource: Condition = withTenantScope({
            resourceType: 'Condition',
            subject: { reference: getReferenceString(patient) },
            encounter: { reference: `Encounter/${encounter.id}` },
            code: value.concept,
            recordedDate: value.recordedDate ?? dayjs().format('YYYY-MM-DD'),
          } as Condition);
          if (value.id) {
            const existing = await medplum.readResource('Condition', value.id);
            const updated = await medplum.updateResource<Condition>({
              ...existing,
              ...resource,
              id: value.id,
              recordedDate: value.recordedDate ?? existing.recordedDate,
            });
            return updated.id ? `Condition/${updated.id}` : undefined;
          }
          const existing = await findExistingCondition(medplum, patient, value.concept);
          if (existing?.id) {
            const updated = await medplum.updateResource<Condition>({
              ...existing,
              code: value.concept,
              id: existing.id,
            });
            return updated.id ? `Condition/${updated.id}` : undefined;
          }
          const created = await medplum.createResource<Condition>(resource);
          return created.id ? `Condition/${created.id}` : undefined;
        };

        const diagnosisRefs = await Promise.all(
          values.diagnoses.map((diagnosis) => upsertDiagnosisCondition(diagnosis))
        );
        const savedDiagnoses = values.diagnoses.map((diagnosis, index) => ({
          ...diagnosis,
          id: diagnosisRefs[index]?.split('/')[1],
        }));
        const encounterDiagnoses = diagnosisRefs.reduce<NonNullable<Encounter['diagnosis']>>((acc, reference) => {
          if (!reference) {
            return acc;
          }

          acc.push({
            condition: { reference },
            rank: acc.length + 1,
            use: { coding: acc.length === 0 ? PRINCIPAL_USE_CODING : SECONDARY_USE_CODING },
          });
          return acc;
        }, []);

        const filteredExtensions = (encounter.extension ?? []).filter((e) => e.url !== POSITIONAL_APNEA_EXTENSION);
        const updatedEncounter: Encounter = {
          ...encounter,
          period: {
            ...encounter.period,
            start: mergeTimeToISO(values.startTime, encounter.period?.start ?? appointment?.start),
            end: mergeTimeToISO(values.endTime, encounter.period?.end ?? appointment?.end),
          },
          reasonCode: values.chiefComplaint ? [{ text: values.chiefComplaint }] : [],
          diagnosis: encounterDiagnoses,
          extension: values.suspectedPositionalApnea
            ? [...filteredExtensions, { url: POSITIONAL_APNEA_EXTENSION, valueString: values.suspectedPositionalApnea }]
            : filteredExtensions,
        };
        const savedEncounter = await medplum.updateResource(updatedEncounter);
        setEncounter(savedEncounter);
        form.setFieldValue('diagnoses', savedDiagnoses);

        // ── 2. Note Observations upsert ───────────────────────────────────────────
        const noteEntries: Array<{ code: NoteCode; text: string }> = [
          { code: 'assessment', text: values.assessment },
          { code: 'hpi', text: values.hpi },
          { code: 'treatment-plan', text: values.treatmentPlan },
          { code: 'vital-signs', text: values.vitalSigns },
          { code: 'review-symptoms', text: values.reviewSymptoms },
          { code: 'exam', text: values.exam },
          { code: 'patient-note', text: values.noteForPatient },
          { code: 'referring-note', text: values.noteForReferring },
        ];
        await Promise.all(
          noteEntries.map(async ({ code, text }) => {
            const existingId = noteObsIdsRef.current.get(code);
            // FHIR rejects an empty `valueString` (surfaces as "Invalid additional property
            // valueString" because the choice-type matcher skips falsy values). Only attach
            // a value when there's non-whitespace content. Skip creating an Observation for a
            // blank note; clear an existing one to a valueless (but valid) Observation.
            const hasText = text.trim().length > 0;
            if (!hasText && !existingId) {
              return;
            }
            const base: Observation = withTenantScope({
              resourceType: 'Observation',
              status: 'final',
              subject: { reference: getReferenceString(patient) },
              encounter: { reference: `Encounter/${encounter.id}` },
              code: { coding: [{ system: NOTE_OBSERVATION_SYSTEM, code }] },
              ...(hasText ? { valueString: text } : {}),
            } as Observation);
            if (existingId) {
              await medplum.updateResource<Observation>({ ...base, id: existingId });
            } else {
              const created = await medplum.createResource<Observation>(base);
              if (created.id) {
                noteObsIdsRef.current.set(code, created.id);
              }
            }
          })
        );

        // ── 3. Symptom Condition CRUD ─────────────────────────────────────────────
        const symptomCurrentIds = new Set(values.symptoms.filter((s) => s.id).map((s) => s.id as string));
        const symptomIdsToDelete = [...symptomIdsRef.current].filter((id) => !symptomCurrentIds.has(id));
        await Promise.all(symptomIdsToDelete.map((id) => medplum.deleteResource('Condition', id)));
        for (const id of symptomIdsToDelete) {
          symptomIdsRef.current.delete(id);
        }

        const updatedSymptoms = await Promise.all(
          values.symptoms.map(async (row): Promise<SymptomFormRow> => {
            if (!row.symptom && !row.snomedConcept) {
              return row;
            }
            const resource: Condition = withTenantScope({
              resourceType: 'Condition',
              subject: { reference: getReferenceString(patient) },
              encounter: { reference: `Encounter/${encounter.id}` },
              category: [{ coding: [{ system: CONDITION_CATEGORY_SYSTEM, code: SYMPTOM_CATEGORY_CODE }] }],
              code: row.snomedConcept ?? { text: row.symptom },
              ...(row.severity ? { severity: { text: row.severity } } : {}),
              ...(row.onset ? { onsetDateTime: dayjs(row.onset).toISOString() } : {}),
              ...(row.frequency ? { note: [{ text: row.frequency }] } : {}),
            } as Condition);
            if (row.id) {
              const existing = await medplum.readResource('Condition', row.id);
              const updated = await medplum.updateResource<Condition>({ ...existing, ...resource, id: row.id });
              if (updated.id) {
                symptomIdsRef.current.add(updated.id);
              }
              return { ...row, id: updated.id };
            }
            const created = await medplum.createResource<Condition>(resource);
            if (created.id) {
              symptomIdsRef.current.add(created.id);
            }
            return { ...row, id: created.id };
          })
        );
        form.setFieldValue('symptoms', updatedSymptoms);

        // ── 4. Comorbidity Condition CRUD ─────────────────────────────────────────
        const comorbidityCurrentIds = new Set(values.comorbidities.filter((c) => c.id).map((c) => c.id as string));
        const comorbidityIdsToDelete = [...comorbidityIdsRef.current].filter((id) => !comorbidityCurrentIds.has(id));
        await Promise.all(comorbidityIdsToDelete.map((id) => medplum.deleteResource('Condition', id)));
        for (const id of comorbidityIdsToDelete) {
          comorbidityIdsRef.current.delete(id);
        }

        const updatedComorbidities = await Promise.all(
          values.comorbidities.map(async (row): Promise<ComorbidityFormRow> => {
            if (!row.icd10Concept) {
              return row;
            }
            const resource: Condition = withTenantScope({
              resourceType: 'Condition',
              subject: { reference: getReferenceString(patient) },
              encounter: { reference: `Encounter/${encounter.id}` },
              category: [{ coding: [{ system: CONDITION_CATEGORY_SYSTEM, code: COMORBIDITY_CATEGORY_CODE }] }],
              code: row.icd10Concept,
              ...(row.onset ? { onsetDateTime: dayjs(row.onset).toISOString() } : {}),
            } as Condition);
            if (row.id) {
              const existing = await medplum.readResource('Condition', row.id);
              const updated = await medplum.updateResource<Condition>({ ...existing, ...resource, id: row.id });
              if (updated.id) {
                comorbidityIdsRef.current.add(updated.id);
              }
              return { ...row, id: updated.id };
            }
            const created = await medplum.createResource<Condition>(resource);
            if (created.id) {
              comorbidityIdsRef.current.add(created.id);
            }
            return { ...row, id: created.id };
          })
        );
        form.setFieldValue('comorbidities', updatedComorbidities);

        // ── 5. MedicationStatement CRUD ───────────────────────────────────────────
        const currentIds = new Set(values.medications.filter((m) => m.id).map((m) => m.id as string));
        const idsToDelete = [...medicationIdsRef.current].filter((id) => !currentIds.has(id));
        await Promise.all(idsToDelete.map((id) => medplum.deleteResource('MedicationStatement', id)));
        for (const id of idsToDelete) {
          medicationIdsRef.current.delete(id);
        }

        const updatedMedications = await Promise.all(
          values.medications.map(async (row): Promise<MedicationFormRow> => {
            if (!row.medicationConcept && !row.dosage && !row.unit && !row.frequency && !row.dateStarted) {
              return row;
            }
            const resource: MedicationStatement = withTenantScope({
              resourceType: 'MedicationStatement',
              status: 'active',
              subject: { reference: getReferenceString(patient) },
              context: { reference: `Encounter/${encounter.id}` },
              medicationCodeableConcept: row.medicationConcept ?? { text: '' },
              effectiveDateTime: row.dateStarted ? dayjs(row.dateStarted).toISOString() : undefined,
              dosage:
                row.dosage || row.unit || row.frequency
                  ? [
                      {
                        text: row.dosage,
                        doseAndRate: row.unit ? [{ doseQuantity: { unit: row.unit } }] : undefined,
                        timing: row.frequency ? { code: { text: row.frequency } } : undefined,
                      },
                    ]
                  : undefined,
            });
            if (row.id) {
              const updated = await medplum.updateResource<MedicationStatement>({ ...resource, id: row.id });
              if (updated.id) {
                medicationIdsRef.current.add(updated.id);
              }
              return { ...row, id: updated.id };
            }
            const created = await medplum.createResource<MedicationStatement>(resource);
            if (created.id) {
              medicationIdsRef.current.add(created.id);
            }
            return { ...row, id: created.id };
          })
        );
        form.setFieldValue('medications', updatedMedications);

        notifications.show({ color: 'green', message: 'Chart saved' });
      } catch (err) {
        showErrorNotification(err);
        throw err;
      }
    },
    [medplum, encounter, appointment, patient, setEncounter, form.setFieldValue]
  );

  // Keep saveChartRef.current pointing at the latest save function.
  // Must call saveToFhir directly (not via form.onSubmit) so the Promise is returned
  // and callers can await the actual save completing before e.g. changing encounter status.
  useEffect(() => {
    if (saveChartRef) {
      saveChartRef.current = () => saveToFhir(form.values);
    }
  });

  // Called by PatientConditionSection once resources are loaded from FHIR
  const handleMedicationsLoaded = useCallback(
    (rows: MedicationFormRow[]) => {
      form.setFieldValue('medications', rows);
      medicationIdsRef.current = new Set(rows.filter((r) => r.id).map((r) => r.id as string));
    },
    [form.setFieldValue]
  );

  const handleSymptomsLoaded = useCallback(
    (rows: SymptomFormRow[]) => {
      form.setFieldValue('symptoms', rows);
      symptomIdsRef.current = new Set(rows.filter((r) => r.id).map((r) => r.id as string));
    },
    [form.setFieldValue]
  );

  const handleComorbiditiesLoaded = useCallback(
    (rows: ComorbidityFormRow[]) => {
      form.setFieldValue('comorbidities', rows);
      comorbidityIdsRef.current = new Set(rows.filter((r) => r.id).map((r) => r.id as string));
    },
    [form.setFieldValue]
  );

  const handleNotesLoaded = useCallback(
    (notes: LoadedNoteObservations) => {
      form.setFieldValue('assessment', notes['assessment'].text);
      form.setFieldValue('hpi', notes.hpi.text);
      form.setFieldValue('treatmentPlan', notes['treatment-plan'].text);
      form.setFieldValue('vitalSigns', notes['vital-signs'].text);
      form.setFieldValue('reviewSymptoms', notes['review-symptoms'].text);
      form.setFieldValue('exam', notes.exam.text);
      form.setFieldValue('noteForPatient', notes['patient-note'].text);
      form.setFieldValue('noteForReferring', notes['referring-note'].text);
      for (const code of NOTE_CODES) {
        const id = notes[code].id;
        if (id) {
          noteObsIdsRef.current.set(code, id);
        }
      }
    },
    [form.setFieldValue]
  );

  const isCompleted = encounter.status === 'finished';
  const handleOpenSign = form.onSubmit(async (values) => {
    await saveToFhir(values);
    openSign();
  });

  return (
    <>
      <Box p="md">
        <Stack gap={10}>
          <SignAddendum encounter={encounter} provenances={provenances} chartNoteStatus={chartNoteStatus} />

          <SupplementalInfoSection patient={patient} encounter={encounter} appointment={appointment} />

          <PatientConditionSection
            form={form}
            patient={patient}
            encounter={encounter}
            disabled={isLocked}
            onMedicationsLoaded={handleMedicationsLoaded}
            onSymptomsLoaded={handleSymptomsLoaded}
            onComorbiditiesLoaded={handleComorbiditiesLoaded}
          />

          <DiagnosesSection
            patient={patient}
            form={form}
            disabled={isLocked}
            codeDisabled={isLocked || chartNoteStatus !== ChartNoteStatus.Unsigned}
          />

          <EncounterNotesSection
            form={form}
            encounter={encounter}
            patient={patient}
            disabled={isLocked}
            onNotesLoaded={handleNotesLoaded}
          />

          {isCompleted && (
            <Card withBorder p={20}>
              <Title order={2} mb={10}>
                Tests
              </Title>
              {serviceRequests.length > 0 && (
                <Stack gap={8} mb={12}>
                  {serviceRequests.map((sr) => (
                    <Group key={sr.id} justify="space-between" p={12} style={{ background: ORDER_BG, borderRadius: 6 }}>
                      <Text size="sm" fw={500}>
                        {sr.code?.text ?? sr.code?.coding?.[0]?.display ?? 'Test'}
                      </Text>
                      <Badge color="grape" variant="light">
                        {sr.status}
                      </Badge>
                    </Group>
                  ))}
                </Stack>
              )}
              <Center
                h={88}
                style={{
                  backgroundColor: ORDER_BG,
                  border: `2px solid ${BRAND}`,
                  borderRadius: 8,
                  cursor: isLocked ? 'not-allowed' : 'pointer',
                  opacity: isLocked ? 0.5 : 1,
                }}
                onClick={isLocked ? undefined : openNewTest}
              >
                <Group gap="xs">
                  <IconFilePlus size={20} color={BRAND} />
                  <Text fw={600} c={BRAND}>
                    Order New Test
                  </Text>
                </Group>
              </Center>
            </Card>
          )}

          {isCompleted && (
            <Card withBorder p={20}>
              <Title order={2} mb={10}>
                Referrals
              </Title>
              {referralRequests.map((referral) => (
                <Group
                  key={referral.id}
                  justify="space-between"
                  p={12}
                  mb={12}
                  style={{ background: ORDER_BG, borderRadius: 6 }}
                >
                  <Text size="sm" fw={500}>
                    {referral.medicationCodeableConcept?.text ?? 'Referral'}
                  </Text>
                  <Badge color="grape" variant="light">
                    {referral.status}
                  </Badge>
                </Group>
              ))}
              {referralRequests.length === 0 && (
                <Text c="dimmed" size="sm" mb={12}>
                  No referrals linked to this encounter.
                </Text>
              )}
              <Center
                h={88}
                style={{
                  backgroundColor: ORDER_BG,
                  border: `2px solid ${BRAND}`,
                  borderRadius: 8,
                  cursor: referralDisabled ? 'not-allowed' : 'pointer',
                  opacity: referralDisabled ? 0.5 : 1,
                }}
                title={canCreateReferral ? undefined : 'Not available for this role'}
                onClick={referralDisabled ? undefined : openReferral}
              >
                <Group gap="xs">
                  <IconFilePlus size={20} color={BRAND} />
                  <Text fw={600} c={BRAND}>
                    Create Referral
                  </Text>
                </Group>
              </Center>
            </Card>
          )}

          {isCompleted && (
            <Card withBorder p={20}>
              <Title order={2} mb={10}>
                Prescriptions
              </Title>
              {therapyRequests.length > 0 && (
                <Stack gap={8} mb={12}>
                  {therapyRequests.map((mr) => (
                    <Group key={mr.id} justify="space-between" p={12} style={{ background: ORDER_BG, borderRadius: 6 }}>
                      <Text size="sm" fw={500}>
                        {mr.medicationCodeableConcept?.text ?? 'Therapy'}
                      </Text>
                      <Badge color="grape" variant="light">
                        {mr.status}
                      </Badge>
                    </Group>
                  ))}
                </Stack>
              )}
              <Center
                h={88}
                style={{
                  backgroundColor: ORDER_BG,
                  border: `2px solid ${BRAND}`,
                  borderRadius: 8,
                  cursor: isLocked ? 'not-allowed' : 'pointer',
                  opacity: isLocked ? 0.5 : 1,
                }}
                onClick={isLocked ? undefined : openNewPrescription}
              >
                <Group gap="xs">
                  <IconFilePlus size={20} color={BRAND} />
                  <Text fw={600} c={BRAND}>
                    Order New Therapy
                  </Text>
                </Group>
              </Center>
            </Card>
          )}

          {isCompleted && (
            <BillingTab
              patient={patient}
              encounter={encounter}
              setEncounter={setEncounter}
              practitioner={practitioner}
              setPractitioner={setPractitioner}
              chargeItems={chargeItems}
              setChargeItems={setChargeItems}
              claim={claim}
              setClaim={setClaim}
              isLocked={isLocked}
            />
          )}

          {isCompleted && (
            <Card withBorder p={20}>
              <Flex align="center" gap={10}>
                <Center
                  flex={1}
                  h={61}
                  style={{
                    border: `2px solid ${BRAND}`,
                    borderRadius: 8,
                    cursor: isLocked ? 'not-allowed' : 'pointer',
                    opacity: isLocked ? 0.5 : 1,
                  }}
                  onClick={
                    isLocked
                      ? undefined
                      : () => {
                          handleOpenSign();
                        }
                  }
                >
                  <Text size="sm" c={BRAND} fw={600}>
                    Signature
                  </Text>
                </Center>
                <Button
                  variant="outline"
                  size="md"
                  style={{ color: BRAND, borderColor: BRAND }}
                  disabled={isLocked}
                  onClick={() => form.onSubmit(saveToFhir)()}
                >
                  Save
                </Button>
                <Button
                  size="md"
                  style={{ backgroundColor: BRAND }}
                  disabled={isLocked}
                  onClick={() => {
                    handleOpenSign();
                  }}
                >
                  Sign
                </Button>
              </Flex>
            </Card>
          )}
        </Stack>
      </Box>

      <Modal opened={signOpened} onClose={closeSign} title="Signing As">
        <SignLockDialog
          onSign={(practitionerRef, lock) => {
            onSign(practitionerRef, lock).catch((error: unknown) => {
              Sentry.captureException(error);
              showErrorNotification(error);
            });
            closeSign();
          }}
        />
      </Modal>

      <OrderNewTestModal
        opened={newTestOpened}
        onClose={handleCloseNewTest}
        encounter={encounter}
        patient={patient}
        practitioner={practitioner}
      />
      <NewPrescriptionModal
        opened={newPrescriptionOpened}
        onClose={handleCloseNewPrescription}
        patient={patient}
        encounter={encounter}
      />
      <ReferralModal
        opened={referralOpened}
        onClose={closeReferral}
        onCreated={() => refreshOrders().catch(console.error)}
        patient={patient}
        encounter={encounter}
        encounterDiagnoses={referralNoteDiagnoses}
      />
    </>
  );
};
